import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  IconButton,
  Divider,
  Switch,
  Tooltip,
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import MessageTemplateService from '../../../../services/messageTemplateService';
import VariableSelector from '../../VariableSelector';

const BlandAIConfig = ({
  config,
  onConfigChange,
  communicationEndpoints,
  onEndpointChange,
  onDelete,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showDispatch, setShowDispatch] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [variableCategories, setVariableCategories] = useState([]);
  const [showVariables, setShowVariables] = useState(false);

  const voices = [
    { id: 'alexa', name: 'Alexa (Female)' },
    { id: 'june', name: 'June (Female)' },
    { id: 'david', name: 'David (Male)' },
    { id: 'paige', name: 'Paige (Female)' },
    { id: 'brady', name: 'Brady (Male)' },
    { id: 'alena', name: 'Alena (Female)' },
    { id: 'allie', name: 'Allie (Female)' },
  ];

  const models = [
    { id: 'base', name: 'Base' },
    { id: 'turbo', name: 'Turbo' },
  ];

  const languages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'es-ES', name: 'Spanish (Spain)' },
    { code: 'es-MX', name: 'Spanish (Mexico)' },
    { code: 'fr-FR', name: 'French' },
    { code: 'de-DE', name: 'German' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'zh-CN', name: 'Chinese (Simplified)' },
    { code: 'zh-TW', name: 'Chinese (Traditional)' },
  ];

  const voicemailDetectionOptions = [
    { value: 'detect', label: 'Detect Voicemail' },
    { value: 'detect_beep', label: 'Detect Beep' },
    { value: 'detect_words', label: 'Detect Words' },
    { value: 'greeting_end', label: 'Greeting End' },
    { value: 'disabled', label: 'Disabled' },
  ];

  const voicemailActions = [
    { value: 'hangup', label: 'Hang Up' },
    { value: 'leave_message', label: 'Leave Message' },
    { value: 'retry_later', label: 'Retry Later' },
  ];

  const webhookEvents = [
    'queue',
    'call',
    'latency',
    'webhook',
    'tool',
    'dynamic_data',
  ];

  const analysisPresets = [
    'default',
    'sales',
    'support',
    'appointment',
    'survey',
    'custom',
  ];

  const geospatialDialingOptions = [
    'disabled',
    'local',
    'national',
    'international',
  ];

  useEffect(() => {
    const fetchAvailableVariables = async () => {
      try {
        const categories = await MessageTemplateService.getAvailableVariables();
        setVariableCategories(categories);
      } catch (err) {
        console.error('Failed to fetch available variables:', err);
      }
    };
    fetchAvailableVariables();
  }, []);

  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    onConfigChange({
      ...config,
      [field]: value,
    });
  };

  const handlePlatformConfigChange = (field, value) => {
    onConfigChange({
      ...config,
      platform_config: {
        ...config.platform_config,
        [field]: value,
      },
    });
  };

  const handleWebhookConfigChange = (field, value) => {
    onConfigChange({
      ...config,
      webhook_config: {
        ...config.webhook_config,
        [field]: value,
      },
    });
  };

  const insertVariable = (categoryName, variableName) => {
    const variable = `{{${categoryName}.${variableName}}}`;
    const currentMessage = config.voicemail_message || '';
    onConfigChange({
      ...config,
      voicemail_message: currentMessage + variable,
    });
  };

  const addDynamicDataItem = () => {
    const currentData = config.platform_config?.dynamic_data || [];
    const newItem = { key: '', value: '' };
    handlePlatformConfigChange('dynamic_data', [...currentData, newItem]);
  };

  const updateDynamicDataItem = (index, field, value) => {
    const currentData = config.platform_config?.dynamic_data || [];
    const updatedData = [...currentData];
    updatedData[index] = { ...updatedData[index], [field]: value };
    handlePlatformConfigChange('dynamic_data', updatedData);
  };

  const removeDynamicDataItem = (index) => {
    const currentData = config.platform_config?.dynamic_data || [];
    const updatedData = currentData.filter((_, i) => i !== index);
    handlePlatformConfigChange('dynamic_data', updatedData);
  };

  const addKeyword = () => {
    const currentKeywords = config.platform_config?.keywords || [];
    handlePlatformConfigChange('keywords', [...currentKeywords, '']);
  };

  const updateKeyword = (index, value) => {
    const currentKeywords = config.platform_config?.keywords || [];
    const updatedKeywords = [...currentKeywords];
    updatedKeywords[index] = value;
    handlePlatformConfigChange('keywords', updatedKeywords);
  };

  const removeKeyword = (index) => {
    const currentKeywords = config.platform_config?.keywords || [];
    const updatedKeywords = currentKeywords.filter((_, i) => i !== index);
    handlePlatformConfigChange('keywords', updatedKeywords);
  };

  const addPronunciationGuide = () => {
    const currentGuide = config.platform_config?.pronunciation_guide || [];
    const newItem = { word: '', pronunciation: '' };
    handlePlatformConfigChange('pronunciation_guide', [...currentGuide, newItem]);
  };

  const updatePronunciationGuide = (index, field, value) => {
    const currentGuide = config.platform_config?.pronunciation_guide || [];
    const updatedGuide = [...currentGuide];
    updatedGuide[index] = { ...updatedGuide[index], [field]: value };
    handlePlatformConfigChange('pronunciation_guide', updatedGuide);
  };

  const removePronunciationGuide = (index) => {
    const currentGuide = config.platform_config?.pronunciation_guide || [];
    const updatedGuide = currentGuide.filter((_, i) => i !== index);
    handlePlatformConfigChange('pronunciation_guide', updatedGuide);
  };

  const addWebhookEvent = (event) => {
    const currentEvents = config.webhook_config?.webhook_events || ['call'];
    if (!currentEvents.includes(event)) {
      handleWebhookConfigChange('webhook_events', [...currentEvents, event]);
    }
  };

  const removeWebhookEvent = (event) => {
    const currentEvents = config.webhook_config?.webhook_events || ['call'];
    const updatedEvents = currentEvents.filter(e => e !== event);
    handleWebhookConfigChange('webhook_events', updatedEvents);
  };

  const addAvailableTag = () => {
    const currentTags = config.platform_config?.available_tags || [];
    handlePlatformConfigChange('available_tags', [...currentTags, '']);
  };

  const updateAvailableTag = (index, value) => {
    const currentTags = config.platform_config?.available_tags || [];
    const updatedTags = [...currentTags];
    updatedTags[index] = value;
    handlePlatformConfigChange('available_tags', updatedTags);
  };

  const removeAvailableTag = (index) => {
    const currentTags = config.platform_config?.available_tags || [];
    const updatedTags = currentTags.filter((_, i) => i !== index);
    handlePlatformConfigChange('available_tags', updatedTags);
  };

  const addTransferListItem = () => {
    const currentList = config.platform_config?.transfer_list || {};
    const newKey = `transfer_${Object.keys(currentList).length + 1}`;
    handlePlatformConfigChange('transfer_list', { ...currentList, [newKey]: '' });
  };

  const updateTransferListItem = (key, value) => {
    const currentList = config.platform_config?.transfer_list || {};
    handlePlatformConfigChange('transfer_list', { ...currentList, [key]: value });
  };

  const removeTransferListItem = (key) => {
    const currentList = config.platform_config?.transfer_list || {};
    const { [key]: removed, ...updatedList } = currentList;
    handlePlatformConfigChange('transfer_list', updatedList);
  };

  const addVoicemailSMSItem = () => {
    const currentSMS = config.platform_config?.voicemail_sms || {};
    const newKey = `sms_${Object.keys(currentSMS).length + 1}`;
    handlePlatformConfigChange('voicemail_sms', { ...currentSMS, [newKey]: '' });
  };

  const updateVoicemailSMSItem = (key, value) => {
    const currentSMS = config.platform_config?.voicemail_sms || {};
    handlePlatformConfigChange('voicemail_sms', { ...currentSMS, [key]: value });
  };

  const removeVoicemailSMSItem = (key) => {
    const currentSMS = config.platform_config?.voicemail_sms || {};
    const { [key]: removed, ...updatedSMS } = currentSMS;
    handlePlatformConfigChange('voicemail_sms', updatedSMS);
  };

  const addDispatchHoursItem = () => {
    const currentHours = config.platform_config?.dispatch_hours || {};
    const newKey = `hours_${Object.keys(currentHours).length + 1}`;
    handlePlatformConfigChange('dispatch_hours', { ...currentHours, [newKey]: '' });
  };

  const updateDispatchHoursItem = (key, value) => {
    const currentHours = config.platform_config?.dispatch_hours || {};
    handlePlatformConfigChange('dispatch_hours', { ...currentHours, [key]: value });
  };

  const removeDispatchHoursItem = (key) => {
    const currentHours = config.platform_config?.dispatch_hours || {};
    const { [key]: removed, ...updatedHours } = currentHours;
    handlePlatformConfigChange('dispatch_hours', updatedHours);
  };

  const addRequestDataItem = () => {
    const currentData = config.platform_config?.request_data || {};
    const newKey = `data_${Object.keys(currentData).length + 1}`;
    handlePlatformConfigChange('request_data', { ...currentData, [newKey]: '' });
  };

  const updateRequestDataItem = (key, value) => {
    const currentData = config.platform_config?.request_data || {};
    handlePlatformConfigChange('request_data', { ...currentData, [key]: value });
  };

  const removeRequestDataItem = (key) => {
    const currentData = config.platform_config?.request_data || {};
    const { [key]: removed, ...updatedData } = currentData;
    handlePlatformConfigChange('request_data', updatedData);
  };

  const addTool = () => {
    const currentTools = config.platform_config?.tools || [];
    const newTool = { name: '', description: '', parameters: {} };
    handlePlatformConfigChange('tools', [...currentTools, newTool]);
  };

  const updateTool = (index, field, value) => {
    const currentTools = config.platform_config?.tools || [];
    const updatedTools = [...currentTools];
    updatedTools[index] = { ...updatedTools[index], [field]: value };
    handlePlatformConfigChange('tools', updatedTools);
  };

  const removeTool = (index) => {
    const currentTools = config.platform_config?.tools || [];
    const updatedTools = currentTools.filter((_, i) => i !== index);
    handlePlatformConfigChange('tools', updatedTools);
  };

  const addMetadataItem = () => {
    const currentMetadata = config.metadata || {};
    const newKey = `meta_${Object.keys(currentMetadata).length + 1}`;
    onConfigChange({
      ...config,
      metadata: { ...currentMetadata, [newKey]: '' }
    });
  };

  const updateMetadataItem = (key, value) => {
    const currentMetadata = config.metadata || {};
    onConfigChange({
      ...config,
      metadata: { ...currentMetadata, [key]: value }
    });
  };

  const removeMetadataItem = (key) => {
    const currentMetadata = config.metadata || {};
    const { [key]: removed, ...updatedMetadata } = currentMetadata;
    onConfigChange({
      ...config,
      metadata: updatedMetadata
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Bland AI Configuration
      </Typography>

      {/* Configuration Summary */}
      <Box sx={{ 
        p: 2, 
        bgcolor: 'background.paper', 
        border: 1, 
        borderColor: 'divider',
        borderRadius: 1, 
        mb: 3,
        boxShadow: 1
      }}>
        <Typography variant="subtitle2" gutterBottom color="text.primary" sx={{ fontWeight: 'bold' }}>
          Configuration Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="error.main" gutterBottom sx={{ fontWeight: 'bold' }}>
              Required Fields:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0, color: 'text.primary' }}>
              <li>Pathway ID</li>
              <li>Voice</li>
              <li>Voicemail Action</li>
              <li>Voicemail Message</li>
              <li>Interruption Threshold</li>
              <li>Temperature</li>
              <li>Webhook</li>
              <li>Webhook Events</li>
              <li>Record</li>
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 'bold' }}>
              Optional Fields:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0, color: 'text.primary' }}>
              <li>Background Track</li>
              <li>Call Behavior Settings</li>
              <li>Voicemail Settings</li>
              <li>Advanced Configuration</li>
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Basic Configuration */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
        Required Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        These fields are required for the Bland AI API call to work properly.
      </Typography>

      {/* Voice Selection */}
      <FormControl fullWidth margin="normal" required>
        <InputLabel>Voice *</InputLabel>
        <Select
          value={config.voice_id || 'alexa'}
          onChange={handleConfigChange('voice_id')}
          label="Voice *"
          error={!config.voice_id}
        >
          {voices.map(voice => (
            <MenuItem key={voice.id} value={voice.id}>
              {voice.name}
            </MenuItem>
          ))}
        </Select>
        {!config.voice_id && (
          <Typography variant="caption" color="error">
            Voice selection is required
          </Typography>
        )}
      </FormControl>

      {/* Task */}
      <TextField
        fullWidth
        margin="normal"
        label="Task (Optional)"
        multiline
        rows={3}
        value={config.platform_config?.task || ''}
        onChange={(e) => handlePlatformConfigChange('task', e.target.value)}
        helperText="The task or instruction for the AI to perform during the call"
      />

      {/* Pathway ID */}
      <TextField
        fullWidth
        margin="normal"
        label="Pathway ID *"
        value={config.platform_config?.pathway_id || ''}
        onChange={(e) => handlePlatformConfigChange('pathway_id', e.target.value)}
        helperText="Bland AI pathway identifier"
        required
        error={!config.platform_config?.pathway_id}
        InputLabelProps={{ required: true }}
      />
      {!config.platform_config?.pathway_id && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          Pathway ID is required to identify the Bland AI pathway
        </Typography>
      )}

      {/* Pathway Version */}
      <FormControlLabel
        control={
          <Checkbox
            checked={config.platform_config?.pathway_version !== undefined && config.platform_config?.pathway_version !== ''}
            onChange={(e) => {
              if (e.target.checked) {
                handlePlatformConfigChange('pathway_version', 1);
              } else {
                handlePlatformConfigChange('pathway_version', undefined);
              }
            }}
          />
        }
        label="Enable Pathway Version"
      />
      {config.platform_config?.pathway_version !== undefined && config.platform_config?.pathway_version !== '' && (
        <TextField
          fullWidth
          margin="normal"
          label="Pathway Version"
          type="number"
          value={config.platform_config?.pathway_version || 1}
          onChange={(e) => handlePlatformConfigChange('pathway_version', parseInt(e.target.value) || 1)}
          helperText="Pathway version number"
        />
      )}

      {/* First Sentence */}
      <TextField
        fullWidth
        margin="normal"
        label="First Sentence (Optional)"
        multiline
        rows={2}
        value={config.platform_config?.first_sentence || ''}
        onChange={(e) => handlePlatformConfigChange('first_sentence', e.target.value)}
        helperText="Opening sentence for the call"
      />



      {/* Optional Settings */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 4 }}>
        Optional Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        These fields enhance the call experience but are not required for basic functionality.
      </Typography>

      {/* Background Track */}
      <TextField
        fullWidth
        margin="normal"
        label="Background Track URL (Optional)"
        value={config.platform_config?.background_track || ''}
        onChange={(e) => handlePlatformConfigChange('background_track', e.target.value)}
        helperText="URL to background audio track"
      />

      {/* Language */}
      <FormControl fullWidth margin="normal">
        <InputLabel>Language (Optional)</InputLabel>
        <Select
          value={config.platform_config?.language || 'en-US'}
          onChange={(e) => handlePlatformConfigChange('language', e.target.value)}
          label="Language (Optional)"
        >
          {languages.map(lang => (
            <MenuItem key={lang.code} value={lang.code}>
              {lang.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Temperature */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Temperature *
        </Typography>
        <Tooltip 
          title={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Temperature Control:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                A value between 0 and 1 that controls the randomness of the LLM. 0 will cause more deterministic outputs while 1 will cause more random.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Example Values:</strong> "0.9", "0.3", "0.5"
              </Typography>
              <Typography variant="body2">
                <strong>Default:</strong> 0.7
              </Typography>
            </Box>
          }
          arrow
          placement="top"
        >
          <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
        </Tooltip>
      </Box>
      <TextField
        fullWidth
        margin="normal"
        type="number"
        value={config.temperature || 0.7}
        onChange={handleConfigChange('temperature')}
        inputProps={{ min: 0, max: 1, step: 0.1 }}
        required
        error={!config.temperature && config.temperature !== 0}
      />
      {!config.temperature && config.temperature !== 0 && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          Temperature is required
        </Typography>
      )}

      {/* Model Selection */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Model (Optional)
        </Typography>
        <Tooltip 
          title={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Bland AI Model Options:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Base:</strong> Follows scripts/procedures most effectively. Supports all features and capabilities. Best for Custom Tools.
              </Typography>
              <Typography variant="body2">
                <strong>Turbo:</strong> Fastest latency possible, can be verbose at times. Limited capabilities (excludes Transferring, IVR navigation, Custom Tools). Extremely realistic conversation capabilities.
              </Typography>
            </Box>
          }
          arrow
          placement="top"
        >
          <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
        </Tooltip>
      </Box>
      <FormControl fullWidth margin="normal">
        <Select
          value={config.platform_config?.model || 'base'}
          onChange={(e) => handlePlatformConfigChange('model', e.target.value)}
        >
          {models.map(model => (
            <MenuItem key={model.id} value={model.id}>
              {model.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>



      {/* Call Behavior */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
        Call Behavior (Optional)
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={config.platform_config?.wait_for_greeting ?? false}
                onChange={(e) => handlePlatformConfigChange('wait_for_greeting', e.target.checked)}
              />
            }
            label="Wait for Greeting"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={config.platform_config?.block_interruptions ?? false}
                onChange={(e) => handlePlatformConfigChange('block_interruptions', e.target.checked)}
              />
            }
            label="Block Interruptions"
          />
        </Grid>
      </Grid>

      {/* Interruption Threshold */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Interruption Threshold *
        </Typography>
        <Tooltip 
          title={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Interruption Threshold:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Adjusts how patient the AI is when waiting for the user to finish speaking.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Recommended range:</strong> 50-200
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • 50: Extremely quick, back and forth conversation
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • 100: Balanced to respond at a natural pace
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • 200: Very patient, allows for long pauses and interruptions. Ideal for collecting detailed information.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Default:</strong> 100
              </Typography>
              <Typography variant="body2">
                Try to start with 100 and make small adjustments in increments of ~10 as needed for your use case.
              </Typography>
            </Box>
          }
          arrow
          placement="top"
        >
          <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
        </Tooltip>
      </Box>
      <TextField
        fullWidth
        margin="normal"
        type="number"
        value={config.platform_config?.interruption_threshold || 100}
        onChange={(e) => handlePlatformConfigChange('interruption_threshold', parseInt(e.target.value) || 0)}
        inputProps={{ min: 0, max: 500 }}
        required
        error={!config.platform_config?.interruption_threshold && config.platform_config?.interruption_threshold !== 0}
      />
      {!config.platform_config?.interruption_threshold && config.platform_config?.interruption_threshold !== 0 && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          Interruption threshold is required
        </Typography>
      )}

      {/* Voicemail Configuration */}
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
        Voicemail Settings (Optional)
      </Typography>

      {/* Voicemail Action */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Voicemail Action *
        </Typography>
        <Tooltip 
          title={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Voicemail Action:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This is processed separately from the AI's decision making, and overrides it.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Options:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • hangup: End the call immediately
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • leave_message: Leave the voicemail message and end call
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • ignore: Continue with the call as normal
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Default:</strong> hangup
              </Typography>
            </Box>
          }
          arrow
          placement="top"
        >
          <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
        </Tooltip>
      </Box>
      <FormControl fullWidth margin="normal" required>
        <Select
          value={config.voicemail_action || 'hangup'}
          onChange={handleConfigChange('voicemail_action')}
          error={!config.voicemail_action}
        >
          {voicemailActions.map(action => (
            <MenuItem key={action.value} value={action.value}>
              {action.label}
            </MenuItem>
          ))}
        </Select>
        {!config.voicemail_action && (
          <Typography variant="caption" color="error">
            Voicemail action is required
          </Typography>
        )}
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={config.platform_config?.sensitive_voicemail_detection ?? false}
            onChange={(e) => handlePlatformConfigChange('sensitive_voicemail_detection', e.target.checked)}
          />
        }
        label="Sensitive Voicemail Detection (Optional)"
      />

      {/* Voicemail Message */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Voicemail Message *
        </Typography>
        <Tooltip 
          title={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Voicemail Message:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                When the AI encounters a voicemail, it will leave this message after the beep and then immediately end the call.
              </Typography>
              <Typography variant="body2">
                <strong>Warning:</strong> If ivr_mode is set to true or voicemail_action is set to ignore, then this will still work for voicemails, but it will not hang up for IVR systems.
              </Typography>
            </Box>
          }
          arrow
          placement="top"
        >
          <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
        </Tooltip>
      </Box>
      <TextField
        fullWidth
        margin="normal"
        multiline
        rows={3}
        value={config.voicemail_message || ''}
        onChange={handleConfigChange('voicemail_message')}
        required
        error={!config.voicemail_message}
      />
      <Box sx={{ mt: 1 }}>
        <VariableSelector
          variableCategories={variableCategories}
          onInsertVariable={insertVariable}
          showVariables={showVariables}
          onToggleVariables={() => setShowVariables(!showVariables)}
        />
      </Box>
      {!config.voicemail_message && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          Voicemail message is required
        </Typography>
      )}

      {/* Advanced Configuration */}
      <Accordion expanded={showAdvanced} onChange={() => setShowAdvanced(!showAdvanced)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Advanced Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Transfer Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Transfer Settings
              </Typography>
              <TextField
                fullWidth
                margin="normal"
                label="Transfer Phone Number"
                value={config.platform_config?.transfer_phone_number || ''}
                onChange={(e) => handlePlatformConfigChange('transfer_phone_number', e.target.value)}
                helperText="Phone number to transfer calls to"
              />
            </Grid>

            {/* Dialing Settings */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.platform_config?.local_dialing ?? false}
                    onChange={(e) => handlePlatformConfigChange('local_dialing', e.target.checked)}
                  />
                }
                label="Local Dialing"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.platform_config?.ignore_button_press ?? false}
                    onChange={(e) => handlePlatformConfigChange('ignore_button_press', e.target.checked)}
                  />
                }
                label="Ignore Button Press"
              />
            </Grid>

            {/* Geospatial Dialing */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.platform_config?.geospatial_dialing !== undefined}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handlePlatformConfigChange('geospatial_dialing', '');
                      } else {
                        handlePlatformConfigChange('geospatial_dialing', undefined);
                      }
                    }}
                  />
                }
                label="Enable Geospatial Dialing"
              />
              {config.platform_config?.geospatial_dialing !== undefined && (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Geospatial Dialing UUID"
                  value={config.platform_config?.geospatial_dialing || ''}
                  onChange={(e) => handlePlatformConfigChange('geospatial_dialing', e.target.value)}
                  helperText="Enter the geospatial dialing pool UUID (Enterprise feature)"
                />
              )}
            </Grid>

            {/* Precall DTMF Sequence */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label="Precall DTMF Sequence"
                value={config.platform_config?.precall_dtmf_sequence || ''}
                onChange={(e) => handlePlatformConfigChange('precall_dtmf_sequence', e.target.value)}
                helperText="DTMF tones to send before the call connects"
              />
            </Grid>

            {/* Timezone */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label="Timezone"
                value={config.platform_config?.timezone || 'America/New_York'}
                onChange={(e) => handlePlatformConfigChange('timezone', e.target.value)}
                helperText="Timezone for the call (e.g., America/New_York)"
              />
            </Grid>

            {/* Duration and Recording */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Max Duration (minutes) (Optional)"
                type="number"
                value={config.max_duration || 5}
                onChange={handleConfigChange('max_duration')}
                inputProps={{ min: 1, max: 60 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.record_call ?? false}
                      onChange={handleConfigChange('record_call')}
                    />
                  }
                  label="Record Call *"
                />
                <Tooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Call Recording:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        To record your phone call, set record to true. When your call completes, you can access through the recording_url field in the call details or your webhook.
                      </Typography>
                      <Typography variant="body2">
                        <strong>Default:</strong> false
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                </Tooltip>
              </Box>
              {!config.record_call && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  Call recording setting is required
                </Typography>
              )}
            </Grid>

            {/* Retry Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Retry Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                If the call goes to voicemail, you can set up the call to retry, after a configurable delay. You can also update the voicemail_action, and voicemail_message in the retry object, for the re-tried call.
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!config.platform_config?.retry}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handlePlatformConfigChange('retry', {
                          wait: 300,
                          voicemail_action: 'hangup',
                          voicemail_message: ''
                        });
                      } else {
                        handlePlatformConfigChange('retry', null);
                      }
                    }}
                  />
                }
                label="Enable Retry Configuration"
              />
              
              {config.platform_config?.retry && (
                <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        margin="normal"
                        label="Retry Wait Time (seconds)"
                        type="number"
                        value={config.platform_config.retry.wait || 300}
                        onChange={(e) => handlePlatformConfigChange('retry', {
                          ...config.platform_config.retry,
                          wait: parseInt(e.target.value) || 0
                        })}
                        inputProps={{ min: 0 }}
                        helperText="The delay in seconds before the call is retried"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Retry Voicemail Action</InputLabel>
                        <Select
                          value={config.platform_config.retry.voicemail_action || 'hangup'}
                          onChange={(e) => handlePlatformConfigChange('retry', {
                            ...config.platform_config.retry,
                            voicemail_action: e.target.value
                          })}
                          label="Retry Voicemail Action"
                        >
                          {voicemailActions.map(action => (
                            <MenuItem key={action.value} value={action.value}>
                              {action.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        margin="normal"
                        label="Retry Voicemail Message"
                        multiline
                        rows={3}
                        value={config.platform_config.retry.voicemail_message || ''}
                        onChange={(e) => handlePlatformConfigChange('retry', {
                          ...config.platform_config.retry,
                          voicemail_message: e.target.value
                        })}
                        helperText="The message to leave when the retried call goes to voicemail"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Grid>

            {/* Call Timeout */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label="Call Timeout (seconds) (Optional)"
                type="number"
                value={config.call_timeout || 30}
                onChange={handleConfigChange('call_timeout')}
                inputProps={{ min: 10, max: 300 }}
              />
            </Grid>



            {/* Analysis Preset */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.platform_config?.analysis_preset !== undefined}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handlePlatformConfigChange('analysis_preset', 'default');
                      } else {
                        handlePlatformConfigChange('analysis_preset', undefined);
                      }
                    }}
                  />
                }
                label="Enable Analysis Preset"
              />
              {config.platform_config?.analysis_preset !== undefined && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Analysis Preset</InputLabel>
                  <Select
                    value={config.platform_config?.analysis_preset || 'default'}
                    onChange={(e) => handlePlatformConfigChange('analysis_preset', e.target.value)}
                    label="Analysis Preset"
                  >
                    {analysisPresets.map(preset => (
                      <MenuItem key={preset} value={preset}>
                        {preset.charAt(0).toUpperCase() + preset.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>

            {/* Citation Schema ID */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label="Citation Schema ID (Optional)"
                value={config.platform_config?.citation_schema_id || ''}
                onChange={(e) => handlePlatformConfigChange('citation_schema_id', e.target.value)}
                helperText="Schema ID for call citations and references"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Transfer List Configuration */}
      <Accordion expanded={showTransfer} onChange={() => setShowTransfer(!showTransfer)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Transfer List Configuration (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Configure transfer options for different scenarios
          </Typography>
          {Object.entries(config.platform_config?.transfer_list || {}).map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="Transfer Key"
                value={key}
                disabled
                sx={{ flex: 1 }}
              />
              <TextField
                label="Phone Number"
                value={value}
                onChange={(e) => updateTransferListItem(key, e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton onClick={() => removeTransferListItem(key)} color="error">
                <RemoveIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addTransferListItem}
            variant="outlined"
            size="small"
          >
            Add Transfer Option
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Voicemail SMS Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Voicemail SMS Configuration (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Configure SMS messages to send when voicemail is detected
          </Typography>
          {Object.entries(config.platform_config?.voicemail_sms || {}).map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="SMS Key"
                value={key}
                disabled
                sx={{ flex: 1 }}
              />
              <TextField
                label="SMS Message"
                value={value}
                onChange={(e) => updateVoicemailSMSItem(key, e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton onClick={() => removeVoicemailSMSItem(key)} color="error">
                <RemoveIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addVoicemailSMSItem}
            variant="outlined"
            size="small"
          >
            Add SMS Message
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Dispatch Hours Configuration */}
      <Accordion expanded={showDispatch} onChange={() => setShowDispatch(!showDispatch)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Dispatch Hours Configuration (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Configure business hours for call dispatching
          </Typography>
          {Object.entries(config.platform_config?.dispatch_hours || {}).map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="Hours Key"
                value={key}
                disabled
                sx={{ flex: 1 }}
              />
              <TextField
                label="Hours Configuration"
                value={value}
                onChange={(e) => updateDispatchHoursItem(key, e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton onClick={() => removeDispatchHoursItem(key)} color="error">
                <RemoveIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addDispatchHoursItem}
            variant="outlined"
            size="small"
          >
            Add Dispatch Hours
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Request Data Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Request Data Configuration (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Additional data to include with the API request
          </Typography>
          {Object.entries(config.platform_config?.request_data || {}).map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="Data Key"
                value={key}
                disabled
                sx={{ flex: 1 }}
              />
              <TextField
                label="Data Value"
                value={value}
                onChange={(e) => updateRequestDataItem(key, e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton onClick={() => removeRequestDataItem(key)} color="error">
                <RemoveIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addRequestDataItem}
            variant="outlined"
            size="small"
          >
            Add Request Data
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Tools Configuration */}
      <Accordion expanded={showTools} onChange={() => setShowTools(!showTools)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Tools Configuration (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Configure tools available to the AI during the call
          </Typography>
          {(config.platform_config?.tools || []).map((tool, index) => (
            <Box key={index} sx={{ border: 1, borderColor: 'divider', p: 2, mb: 2, borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tool Name"
                    value={tool.name || ''}
                    onChange={(e) => updateTool(index, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Tool Description"
                    value={tool.description || ''}
                    onChange={(e) => updateTool(index, 'description', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Tool Parameters (JSON)"
                    value={JSON.stringify(tool.parameters || {}, null, 2)}
                    onChange={(e) => {
                      try {
                        const params = JSON.parse(e.target.value);
                        updateTool(index, 'parameters', params);
                      } catch (error) {
                        // Invalid JSON, keep as string
                      }
                    }}
                    helperText="JSON format for tool parameters"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<RemoveIcon />}
                    onClick={() => removeTool(index)}
                    size="small"
                  >
                    Remove Tool
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addTool}
            variant="outlined"
            size="small"
          >
            Add Tool
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Dynamic Data */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Dynamic Data (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Key-value pairs to pass to the AI model during the call
          </Typography>
          {(config.platform_config?.dynamic_data || []).map((item, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="Key"
                value={item.key || ''}
                onChange={(e) => updateDynamicDataItem(index, 'key', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Value"
                value={item.value || ''}
                onChange={(e) => updateDynamicDataItem(index, 'value', e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton onClick={() => removeDynamicDataItem(index)} color="error">
                <RemoveIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addDynamicDataItem}
            variant="outlined"
            size="small"
          >
            Add Dynamic Data
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Keywords */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Keywords (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Keywords to help guide the conversation
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {(config.platform_config?.keywords || []).map((keyword, index) => (
              <Chip
                key={index}
                label={keyword}
                onDelete={() => removeKeyword(index)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Add Keyword"
              size="small"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  const currentKeywords = config.platform_config?.keywords || [];
                  handlePlatformConfigChange('keywords', [...currentKeywords, e.target.value.trim()]);
                  e.target.value = '';
                }
              }}
            />
            <Button
              startIcon={<AddIcon />}
              onClick={addKeyword}
              variant="outlined"
              size="small"
            >
              Add
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Pronunciation Guide */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Pronunciation Guide (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Custom pronunciation for specific words
          </Typography>
          {(config.platform_config?.pronunciation_guide || []).map((item, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="Word"
                value={item.word || ''}
                onChange={(e) => updatePronunciationGuide(index, 'word', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Pronunciation"
                value={item.pronunciation || ''}
                onChange={(e) => updatePronunciationGuide(index, 'pronunciation', e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton onClick={() => removePronunciationGuide(index)} color="error">
                <RemoveIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addPronunciationGuide}
            variant="outlined"
            size="small"
          >
            Add Pronunciation Guide
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Available Tags */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Available Tags (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Tags that can be applied during the call for analysis
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {(config.platform_config?.available_tags || []).map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => removeAvailableTag(index)}
                color="secondary"
                variant="outlined"
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Add Tag"
              size="small"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  const currentTags = config.platform_config?.available_tags || [];
                  handlePlatformConfigChange('available_tags', [...currentTags, e.target.value.trim()]);
                  e.target.value = '';
                }
              }}
            />
            <Button
              startIcon={<AddIcon />}
              onClick={addAvailableTag}
              variant="outlined"
              size="small"
            >
              Add
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Metadata Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Metadata Configuration (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Additional metadata to include with the call
          </Typography>
          {Object.entries(config.metadata || {}).map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="Metadata Key"
                value={key}
                disabled
                sx={{ flex: 1 }}
              />
              <TextField
                label="Metadata Value"
                value={value}
                onChange={(e) => updateMetadataItem(key, e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton onClick={() => removeMetadataItem(key)} color="error">
                <RemoveIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addMetadataItem}
            variant="outlined"
            size="small"
          >
            Add Metadata
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Webhook Configuration */}
      <Accordion expanded={showWebhook} onChange={() => setShowWebhook(!showWebhook)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Webhook Configuration *</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Webhook URL *
            </Typography>
            <Tooltip 
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Webhook URL:
                  </Typography>
                  <Typography variant="body2">
                    When the call ends, call information is sent to this webhook URL.
                  </Typography>
                </Box>
              }
              arrow
              placement="top"
            >
              <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
            </Tooltip>
          </Box>
          <TextField
            fullWidth
            margin="normal"
            value={config.webhook_config?.webhook || ''}
            onChange={(e) => handleWebhookConfigChange('webhook', e.target.value)}
            required
            error={!config.webhook_config?.webhook}
          />
          {!config.webhook_config?.webhook && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              Webhook URL is required
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2">
              Webhook Events *
            </Typography>
            <Tooltip 
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Webhook Events:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Specify which events you want to stream to the webhook, during the call.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Options:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • queue
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • call (we always default to call)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • latency
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • webhook
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • tool
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • dynamic_data
                  </Typography>
                  <Typography variant="body2">
                    • citations (Sent separately, Enterprise only)
                  </Typography>
                </Box>
              }
              arrow
              placement="top"
            >
              <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {webhookEvents.map(event => {
              const isSelected = (config.webhook_config?.webhook_events || ['call']).includes(event);
              return (
                <Chip
                  key={event}
                  label={event}
                  onClick={() => isSelected ? removeWebhookEvent(event) : addWebhookEvent(event)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  clickable
                />
              );
            })}
          </Box>
          {(!config.webhook_config?.webhook_events || config.webhook_config.webhook_events.length === 0) && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              At least one webhook event is required
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Alert severity="info" sx={{ mt: 2 }}>
        Bland AI provides comprehensive voice AI capabilities with advanced features including 
        dynamic data, pronunciation guides, webhooks, tools, and extensive call analytics.
      </Alert>

      {onDelete && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          sx={{ mt: 2 }}
        >
          Remove Voice Configuration
        </Button>
      )}
    </Box>
  );
};

BlandAIConfig.propTypes = {
  config: PropTypes.object.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  communicationEndpoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      channel_list: PropTypes.arrayOf(PropTypes.string).isRequired,
      platform: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        display_name: PropTypes.string.isRequired,
        platform_type: PropTypes.string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  onEndpointChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

export default BlandAIConfig; 